import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    const formData = new URLSearchParams();
    formData.append("username", username);
    formData.append("password", password);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Invalid credentials");
      }

      const data = await res.json();
      const { access_token, user } = data;

      // Save auth data
      localStorage.setItem("authToken", access_token);
      localStorage.setItem("userRole", user.role);
      localStorage.setItem("userId", user.id.toString());
      localStorage.setItem("username", user.username);

      setMessage("✅ Login successful!");

      // ✅ SMART REDIRECT BASED ON ROLE
      if (user.role === "driver") {
        navigate("/delivery");
      } else if (["admin", "manager"].includes(user.role)) {
        navigate("/dashboard");
      } else {
        // All other roles (user, cashier, staff, etc.)
        navigate("/home");
      }
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background: "radial-gradient(circle at 20% 20%, #1a1c3e 0%, #0a0c1f 55%, #05060f 100%)",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* Ambient glow orbs */}
      <div
        style={{
          position: "absolute",
          top: "-120px",
          left: "-100px",
          width: "320px",
          height: "320px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(247,107,76,0.18) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-140px",
          right: "-100px",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(76,140,247,0.16) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <style>{`
        @keyframes borderGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(247,107,76,0.25), 0 0 60px rgba(76,140,247,0.12); }
          50% { box-shadow: 0 0 28px rgba(247,107,76,0.4), 0 0 80px rgba(76,140,247,0.22); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .login-card {
          animation: fadeUp 0.5s ease-out, borderGlow 4s ease-in-out infinite;
        }
        .login-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(245,233,216,0.15);
          color: #F5E9D8;
        }
        .login-input::placeholder {
          color: rgba(245,233,216,0.35);
        }
        .login-input:focus {
          outline: none;
          border-color: #F76B4C;
          box-shadow: 0 0 0 3px rgba(247,107,76,0.2);
        }
        @media (max-width: 400px) {
          .login-card { padding: 1.5rem !important; }
          .login-title { font-size: 1.4rem !important; }
        }
      `}</style>

      <div
        className="login-card"
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "rgba(20,22,48,0.85)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(245,233,216,0.1)",
          borderRadius: "18px",
          padding: "2.2rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "1.8rem" }}>
          <div style={{ fontSize: "2.4rem", marginBottom: "0.4rem" }}>🍞</div>
          <h2
            className="login-title"
            style={{
              color: "#F5E9D8",
              fontWeight: 800,
              fontSize: "1.6rem",
              letterSpacing: "0.5px",
              margin: 0,
            }}
          >
            Pyszna Food ERP
          </h2>
          <p style={{ color: "rgba(245,233,216,0.5)", fontSize: "0.85rem", marginTop: "0.4rem" }}>
            Sign in to your account
          </p>
        </div>

        <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="login-input"
            style={{ width: "100%", padding: "0.85rem 1rem", borderRadius: "10px", fontSize: "0.95rem" }}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
            style={{ width: "100%", padding: "0.85rem 1rem", borderRadius: "10px", fontSize: "0.95rem" }}
            required
          />

          <div style={{ textAlign: "right" }}>
            <a
              href="/forgot-password"
              style={{ fontSize: "0.8rem", color: "#F76B4C", textDecoration: "none" }}
            >
              Forgot Password?
            </a>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "0.9rem",
              borderRadius: "10px",
              border: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "white",
              cursor: loading ? "not-allowed" : "pointer",
              background: loading
                ? "rgba(247,107,76,0.5)"
                : "linear-gradient(135deg, #F76B4C 0%, #e55a3c 100%)",
              boxShadow: "0 4px 20px rgba(247,107,76,0.35)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseOver={(e) => {
              if (!loading) e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {loading ? "Signing in…" : "Login"}
          </button>
        </form>

        {message && (
          <div
            style={{
              marginTop: "1rem",
              textAlign: "center",
              padding: "0.75rem",
              borderRadius: "10px",
              fontSize: "0.9rem",
              background: message.includes("✅")
                ? "rgba(46,204,113,0.12)"
                : "rgba(231,76,60,0.12)",
              color: message.includes("✅") ? "#2ecc71" : "#e74c3c",
              border: message.includes("✅")
                ? "1px solid rgba(46,204,113,0.3)"
                : "1px solid rgba(231,76,60,0.3)",
            }}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
}